export type GooglePayProbeStatus =
  | 'present'
  | 'absent'
  | 'rejected'
  | 'sdk-unavailable'
  | 'timeout';

export interface GooglePayProbeResult {
  ready?: boolean;
  status?: GooglePayProbeStatus;
  error?: string;
}

const GOOGLE_PAY_SDK_URL = 'https://pay.google.com/gp/p/js/pay.js';
const GOOGLE_PAY_TIMEOUT_MS = 1800;

type IsReadyToPayRequest = {
  apiVersion: 2;
  apiVersionMinor: 0;
  existingPaymentMethodRequired: true;
  allowedPaymentMethods: [
    {
      type: 'CARD';
      parameters: {
        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'];
        allowedCardNetworks: ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'];
      };
    },
  ];
};

type PaymentsClient = new (options: { environment: 'PRODUCTION' }) => {
  isReadyToPay(request: IsReadyToPayRequest): Promise<{ paymentMethodPresent?: boolean }>;
};

type GooglePayGlobal = {
  payments?: { api?: { PaymentsClient?: PaymentsClient } };
};

let sdkPromise: Promise<void> | null = null;

function googlePayGlobal(): GooglePayGlobal | undefined {
  const value: unknown = (window as Window & { google?: unknown }).google;
  if (!value || typeof value !== 'object') return undefined;
  return value as GooglePayGlobal;
}

function getPaymentsClient(): PaymentsClient | undefined {
  return googlePayGlobal()?.payments?.api?.PaymentsClient;
}

async function waitForSdk(): Promise<void> {
  if (getPaymentsClient()) return;
  if (!sdkPromise) {
    const script = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_PAY_SDK_URL}"]`);
    if (!script) {
      const injected = document.createElement('script');
      injected.async = true;
      injected.src = GOOGLE_PAY_SDK_URL;
      document.head.appendChild(injected);
    }
    sdkPromise = (async () => {
      const deadline = performance.now() + GOOGLE_PAY_TIMEOUT_MS;
      while (!getPaymentsClient() && performance.now() < deadline) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 50));
      }
      if (!getPaymentsClient()) throw new Error('Google Pay SDK unavailable');
    })().catch((error) => {
      sdkPromise = null;
      throw error;
    });
  }
  await sdkPromise;
}

const request: IsReadyToPayRequest = {
  apiVersion: 2,
  apiVersionMinor: 0,
  existingPaymentMethodRequired: true,
  allowedPaymentMethods: [
    {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
        allowedCardNetworks: ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'],
      },
    },
  ],
};

/** Android-only Google Pay probe; every outcome is observable and score-neutral. */
export async function checkGooglePayReady(
  ua: string = navigator.userAgent,
): Promise<GooglePayProbeResult> {
  if (!/android/i.test(ua)) return {};
  try {
    await waitForSdk();
    const PaymentsClient = getPaymentsClient();
    if (!PaymentsClient) return { ready: false, status: 'sdk-unavailable' };
    const result = await new PaymentsClient({ environment: 'PRODUCTION' }).isReadyToPay(request);
    return result.paymentMethodPresent === true
      ? { ready: true, status: 'present' }
      : { ready: false, status: 'absent' };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      ready: false,
      status:
        message.includes('timed out') || message.includes('unavailable') ? 'timeout' : 'rejected',
      error: message,
    };
  }
}
